// ==================== REUSABLE UTILITIES ====================

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} container - The container element to search within
 * @returns {HTMLElement[]} Array of focusable elements
 */
const getFocusableElements = (container) => {
  return Array.from(
    container.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter(el => el.offsetParent !== null);
};

/**
 * Create a toggle panel with ARIA attributes and accessibility support
 * @param {HTMLElement} trigger - The button/element that triggers the panel
 * @param {HTMLElement} panel - The panel element to show/hide
 * @param {Object} options - Configuration options
 * @param {string} options.activeClass - CSS class to add when open (default: 'active')
 * @param {Function} options.onOpen - Callback when panel opens
 * @param {Function} options.onClose - Callback when panel closes
 * @param {boolean} options.closeOnOutsideClick - Close when clicking outside (default: true)
 * @returns {Object} Methods to control the panel: { open, close, toggle, isOpen }
 */
const createTogglePanel = (trigger, panel, options = {}) => {
  const {
    activeClass = 'active',
    onOpen = null,
    onClose = null,
    closeOnOutsideClick = true
  } = options;

  const isOpen = () => panel.classList.contains(activeClass);

  const open = () => {
    panel.classList.add(activeClass);
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    onOpen?.();
  };

  const close = () => {
    panel.classList.remove(activeClass);
    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');
    onClose?.();
  };

  const toggle = () => isOpen() ? close() : open();

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    toggle();
  });

  if (closeOnOutsideClick) {
    document.addEventListener('click', (e) => {
      if (isOpen() && !panel.contains(e.target) && !trigger.contains(e.target)) {
        close();
      }
    });
  }

  return { open, close, toggle, isOpen };
};

/**
 * Create a focus trap for keyboard navigation within a container
 * Uses document-level listeners (capture phase) for maximum reliability.
 * Must be explicitly activated/deactivated.
 * @param {HTMLElement} container - The container to trap focus within
 * @param {Object} options - Configuration options
 * @param {Function} options.onEscape - Callback when Escape is pressed
 * @param {HTMLElement} options.returnFocusOnClose - Element to focus when Escape is pressed
 * @returns {Object} Methods: { activate, deactivate, focusFirst }
 */
const createFocusTrap = (container, options = {}) => {
  const {
    onEscape = null,
    returnFocusOnClose = null
  } = options;

  let active = false;

  const getElements = () => getFocusableElements(container);

  const handleKeyDown = (e) => {
    if (!active) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      onEscape?.();
      returnFocusOnClose?.focus();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = getElements();
    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Also handle when focus is on the container itself (e.g. after container.focus())
    if (e.shiftKey) {
      if (document.activeElement === firstElement || document.activeElement === container) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement || document.activeElement === container) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  // Guard: if focus escapes the container by any means, pull it back
  const handleFocusIn = (e) => {
    if (!active) return;
    if (!container.contains(e.target)) {
      e.stopImmediatePropagation();
      const focusableElements = getElements();
      if (focusableElements.length) {
        focusableElements[0].focus();
      } else {
        container.focus();
      }
    }
  };

  const activate = () => {
    if (active) return;
    active = true;
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn);
  };

  const deactivate = () => {
    if (!active) return;
    active = false;
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('focusin', handleFocusIn);
  };

  const focusFirst = () => {
    const els = getElements();
    if (els.length) els[0].focus();
    else container.focus();
  };

  const isActive = () => active;

  return { activate, deactivate, focusFirst, isActive };
};

/**
 * Create keyboard navigation for dropdown menus
 * Handles Enter, Space, Arrow keys, Escape, and Tab navigation
 * @param {HTMLElement} trigger - The button that opens the dropdown
 * @param {HTMLElement} menu - The dropdown menu element
 * @param {Object} options - Configuration options
 * @param {string} options.openClass - CSS class to add when open (default: 'open')
 * @param {Function} options.onOpen - Callback when menu opens
 * @param {Function} options.onClose - Callback when menu closes
 * @returns {Object} Methods: { open, close, toggle }
 */
const createDropdownNavigation = (trigger, menu, options = {}) => {
  const {
    openClass = 'open',
    onOpen = null,
    onClose = null
  } = options;

  let isOpen = false;

  const getItems = () => Array.from(menu.querySelectorAll('a, button:not([disabled])'));

  const open = ({ focusFirst = true } = {}) => {
    isOpen = true;
    trigger.setAttribute('aria-expanded', 'true');
    if (openClass) menu.classList.add(openClass);
    onOpen?.({ focusFirst });
    if (focusFirst) getItems()[0]?.focus();
  };

  const close = () => {
    isOpen = false;
    trigger.setAttribute('aria-expanded', 'false');
    if (openClass) menu.classList.remove(openClass);
    onClose?.();
  };

  const toggle = () => isOpen ? close() : open();

  // Click handler for trigger button
  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  });

  // Keyboard handler for trigger button
  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      open();
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      close();
      trigger.focus();
    }
  });

  // Keyboard navigation within menu items
  menu.addEventListener('keydown', (e) => {
    const items = getItems();
    const currentIndex = items.indexOf(document.activeElement);

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        trigger.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        items[(currentIndex + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[(currentIndex - 1 + items.length) % items.length]?.focus();
        break;
      case 'Tab':
        if (!e.shiftKey && currentIndex === items.length - 1) {
          close();
        } else if (e.shiftKey && currentIndex === 0) {
          e.preventDefault();
          close();
          trigger.focus();
        }
        break;
    }
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (isOpen && !trigger.contains(e.target) && !menu.contains(e.target)) {
      close();
    }
  });

  return { open, close, toggle };
};

// ==================== SEARCH SECTION ====================

const searchIcon = document.querySelector("[data-search-link]");
const searchView = document.querySelector(".header__quick-search-bar");
const searchClear = document.querySelector("header [data-quick-search-clear]");
const searchInput = searchView?.querySelector('[data-quick-search-input]');

if (searchIcon && searchView) {
  let searchFocusTrap;

  // Start with inert so Tab cannot reach it when closed
  searchView.setAttribute('inert', '');

  const { open: openSearch, close: closeSearch } = createTogglePanel(searchIcon, searchView, {
    onOpen: () => {
      searchView.removeAttribute('inert');
      searchClear?.click();
      searchFocusTrap?.activate();
      // Focus the input after a tick so the panel is visible
      requestAnimationFrame(() => {
        searchInput?.focus();
      });
    },
    onClose: () => {
      searchFocusTrap?.deactivate();
      searchView.setAttribute('inert', '');
      searchIcon.focus();
    }
  });

  searchFocusTrap = createFocusTrap(searchView, {
    returnFocusOnClose: searchIcon,
    onEscape: closeSearch
  });
}

// ==================== CART SECTION ====================

const cartIcon = document.querySelector("[data-cart-link]");
const cartView = document.querySelector(".quick-cart__wrapper");
const cartCloseButton = document.querySelector(".quick-cart__close-icon");

if (cartIcon && cartView) {
  let focusTrap;

  const { open, close } = createTogglePanel(cartIcon, cartView, {
    onOpen: () => {
      focusTrap?.activate();
      (cartCloseButton || getFocusableElements(cartView)[0])?.focus();
    },
    onClose: () => {
      focusTrap?.deactivate();
      cartIcon.focus();
    },
    closeOnOutsideClick: false // Handled manually below
  });

  focusTrap = createFocusTrap(cartView, {
    returnFocusOnClose: cartIcon,
    onEscape: () => {
      // Trigger the close button click so theme.js's delegate handler
      // also runs (deactivates its own focus trap + re-enables body scroll)
      const btn = cartView.querySelector('.quick-cart__close-icon');
      if (btn) {
        btn.click();
      } else {
        close();
      }
    }
  });

  // Close with close button
  cartCloseButton?.addEventListener('click', close);

  // Close on overlay click
  cartView.querySelector('.quick-cart__overlay')?.addEventListener('click', close);

  // Close when clicking outside (only on inner container)
  document.addEventListener('click', (e) => {
    if (cartView.classList.contains('active') &&
      !cartView.querySelector('.quick-cart__container')?.contains(e.target) &&
      !cartIcon.contains(e.target)) {
      close();
    }
  });

  // ── Sync: when theme.js opens the cart (e.g. via add-to-cart) without
  //    going through our createTogglePanel, we still need the focus trap
  //    and Escape support. Observe the "active" class on cartView. ──
  const cartClassObserver = new MutationObserver(() => {
    const isActive = cartView.classList.contains('active');
    if (isActive && !focusTrap.isActive()) {
      // Cart was opened externally (theme.js add-to-cart flow)
      focusTrap.activate();
      (cartCloseButton || getFocusableElements(cartView)[0])?.focus();
    } else if (!isActive && focusTrap.isActive()) {
      // Cart was closed externally
      focusTrap.deactivate();
    }
  });
  cartClassObserver.observe(cartView, { attributes: true, attributeFilter: ['class'] });
}

// ==================== ACCOUNT SECTION (FLYOUT) ====================
// NOTE: theme.min.js already handles the click toggle on [data-account-link].
// We only layer on focus trap + Escape key support via MutationObserver
// to avoid conflicting with theme.min.js's stopPropagation() click handler.

const accountTrigger = document.querySelector('[data-account-link]');
const accountFlyout = document.querySelector('[data-account-quick-view]');

if (accountTrigger && accountFlyout) {
  let accountTrapActive = false;

  const closeAccount = () => {
    accountFlyout.classList.remove('active');
    accountTrigger.classList.remove('active');
    accountFlyout.setAttribute('aria-hidden', 'true');
    accountTrigger.setAttribute('aria-expanded', 'false');
    accountTrigger.focus();
  };

  const handleAccountKeyDown = (e) => {
    if (!accountTrapActive) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeAccount();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = getFocusableElements(accountFlyout);
    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || document.activeElement === accountFlyout) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last || document.activeElement === accountFlyout) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const activateAccountTrap = () => {
    if (accountTrapActive) return;
    accountTrapActive = true;
    document.addEventListener('keydown', handleAccountKeyDown, true);
  };

  const deactivateAccountTrap = () => {
    if (!accountTrapActive) return;
    accountTrapActive = false;
    document.removeEventListener('keydown', handleAccountKeyDown, true);
  };

  // Watch for class changes made by theme.min.js to detect open/close
  new MutationObserver(() => {
    const isOpen = accountFlyout.classList.contains('active');
    if (isOpen && !accountTrapActive) {
      activateAccountTrap();
      accountFlyout.focus();
    } else if (!isOpen && accountTrapActive) {
      deactivateAccountTrap();
    }
  }).observe(accountFlyout, {
    attributes: true,
    attributeFilter: ['class']
  });
}

// ==================== DESKTOP MENU ====================

const menuItems = document.querySelectorAll('.header-menu-desktop .menu-item');
let currentOpenMenu = null;
let closeTimeout = null;

/**
 * Close all open menus
 */
const closeAllMenus = () => {
  clearTimeout(closeTimeout);
  menuItems.forEach(item => {
    const submenu = document.getElementById('menu-' + item.getAttribute('data-id'));
    if (submenu) {
      submenu.classList.remove('open');
      item.setAttribute('aria-expanded', 'false');
    }
  });
  currentOpenMenu = null;
};

/**
 * Open a specific menu and close others
 * @param {HTMLElement} menuButton - The menu button to open
 */
const openMenu = (menuButton) => {
  clearTimeout(closeTimeout);

  // Close other menus
  menuItems.forEach(item => {
    if (item !== menuButton) {
      const submenu = document.getElementById('menu-' + item.getAttribute('data-id'));
      if (submenu) {
        submenu.classList.remove('open');
        item.setAttribute('aria-expanded', 'false');
      }
    }
  });

  // Open target menu
  const submenu = document.getElementById('menu-' + menuButton.getAttribute('data-id'));
  if (submenu) {
    submenu.classList.add('open');
    menuButton.setAttribute('aria-expanded', 'true');
    currentOpenMenu = menuButton;
  }
};

/**
 * Toggle a menu open or closed
 * @param {HTMLElement} menuButton - The menu button to toggle
 */
const toggleMenu = (menuButton) => {
  const submenu = document.getElementById('menu-' + menuButton.getAttribute('data-id'));
  if (submenu?.classList.contains('open')) {
    closeAllMenus();
  } else {
    openMenu(menuButton);
  }
};

// Event listeners for items with submenus
menuItems.forEach((item) => {
  if (!item.hasAttribute('aria-haspopup')) return;

  const getSubmenu = () => document.getElementById('menu-' + item.getAttribute('data-id'));

  // Hover handlers
  item.addEventListener('mouseenter', () => {
    clearTimeout(closeTimeout);
    openMenu(item);
  });

  item.addEventListener('mouseleave', () => {
    closeTimeout = setTimeout(() => {
      const submenu = getSubmenu();
      if (submenu && !submenu.matches(':hover')) closeAllMenus();
    }, 100);
  });

  // Click handler
  item.addEventListener('click', (e) => {
    e.preventDefault();
    toggleMenu(item);
  });

  // Keyboard handler
  item.addEventListener('keydown', (e) => {
    const submenu = getSubmenu();

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        toggleMenu(item);
        if (submenu?.classList.contains('open')) {
          submenu.querySelector('a')?.focus();
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeAllMenus();
        item.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!submenu?.classList.contains('open')) openMenu(item);
        submenu?.querySelector('a')?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        closeAllMenus();
        break;
    }
  });
});

// Submenu hover handlers
document.querySelectorAll('.menu-desktop').forEach(submenu => {
  submenu.addEventListener('mouseenter', () => clearTimeout(closeTimeout));
  submenu.addEventListener('mouseleave', () => {
    closeTimeout = setTimeout(closeAllMenus, 100);
  });
});

// Keyboard navigation within submenus
document.addEventListener('keydown', (e) => {
  if (!currentOpenMenu) return;

  const submenu = document.getElementById('menu-' + currentOpenMenu.getAttribute('data-id'));
  if (!submenu?.classList.contains('open')) return;

  const items = Array.from(submenu.querySelectorAll('a, button'));
  const currentIndex = items.indexOf(document.activeElement);

  if (currentIndex === -1) return;

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      items[currentIndex + 1]?.focus();
      break;
    case 'ArrowUp':
      e.preventDefault();
      if (currentIndex > 0) {
        items[currentIndex - 1].focus();
      } else {
        currentOpenMenu.focus();
        closeAllMenus();
      }
      break;
    case 'Escape':
      e.preventDefault();
      closeAllMenus();
      currentOpenMenu.focus();
      break;
    case 'Tab':
      const isLast = currentIndex === items.length - 1;
      const isFirst = currentIndex === 0;

      if (isLast && !e.shiftKey) {
        e.preventDefault();
        closeAllMenus();
        const nextItem = menuItems[Array.from(menuItems).indexOf(currentOpenMenu) + 1];
        if (nextItem) {
          nextItem.focus();
        } else {
          // Find next focusable element outside nav
          const nav = document.querySelector('.header-menu-desktop');
          const allFocusable = getFocusableElements(document.body);
          const navIndex = allFocusable.findIndex(el => nav.contains(el));
          allFocusable.find((el, idx) => idx > navIndex && !nav.contains(el))?.focus();
        }
      } else if (isFirst && e.shiftKey) {
        e.preventDefault();
        closeAllMenus();
        currentOpenMenu.focus();
      }
      break;
  }
});

// Close when clicking outside
document.addEventListener('click', (e) => {
  if (!e.target.closest('.header-menu-desktop .menu-item') &&
    !e.target.closest('.menu-desktop')) {
    closeAllMenus();
  }
});

// ==================== DESKTOP LANGUAGE SELECTOR ====================

/**
 * Initialize the desktop language selector dropdown
 * Handles keyboard navigation, hover functionality, and focus trap
 */
const setupDesktopLanguageDropdown = () => {
  const dropdownButton = document.getElementById('desktop-language-selector-button');
  const dropdownMenu = document.getElementById('desktop-language-selector-menu');

  if (!dropdownButton || !dropdownMenu) return;

  // Ensure menu is hidden initially
  dropdownMenu.style.display = 'none';

  let focusTrap;

  const dropdown = createDropdownNavigation(dropdownButton, dropdownMenu, {
    openClass: 'show',
    onOpen: ({ focusFirst = true } = {}) => {
      dropdownMenu.style.display = 'flex';
      dropdownMenu.removeAttribute('inert');
      dropdownMenu.setAttribute('aria-hidden', 'false');
      focusTrap?.activate();
      // Only focus first item on keyboard navigation, not on hover
      if (focusFirst) {
        setTimeout(() => {
          const firstItem = getFocusableElements(dropdownMenu)[0];
          firstItem?.focus();
        }, 10);
      }
    },
    onClose: () => {
      dropdownMenu.style.display = 'none';
      dropdownMenu.setAttribute('inert', '');
      dropdownMenu.setAttribute('aria-hidden', 'true');
      focusTrap?.deactivate();
    }
  });

  // Create focus trap for the dropdown menu
  focusTrap = createFocusTrap(dropdownMenu, {
    returnFocusOnClose: dropdownButton,
    onEscape: () => {
      dropdown.close();
      dropdownButton.focus();
    }
  });

  // Set initial inert state
  dropdownMenu.setAttribute('inert', '');
  dropdownMenu.setAttribute('aria-hidden', 'true');

  // Additional hover functionality
  const dropdownContainer = dropdownButton.closest('.dropdown');
  if (dropdownContainer) {
    dropdownContainer.addEventListener('mouseenter', () => dropdown.open({ focusFirst: false }));
    dropdownContainer.addEventListener('mouseleave', () => {
      dropdown.close();
      // Ensure focus returns to button when closed via mouseleave
      if (dropdownMenu.contains(document.activeElement)) {
        dropdownButton.focus();
      }
    });
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupDesktopLanguageDropdown);
} else {
  setupDesktopLanguageDropdown();
}

// ==================== MOBILE LANGUAGE SELECTOR ====================

/**
 * Initialize the mobile language selector dropdown
 * Handles keyboard navigation and focus trap
 */
const setupMobileLanguageDropdown = () => {
  const dropdownButton = document.getElementById('language-selector-button');
  const dropdownMenu = document.getElementById('language-selector-menu');

  if (!dropdownButton || !dropdownMenu) return;

  // Ensure menu is hidden initially
  dropdownMenu.style.display = 'none';

  let focusTrap;

  const dropdown = createDropdownNavigation(dropdownButton, dropdownMenu, {
    openClass: 'show',
    onOpen: () => {
      dropdownMenu.style.display = 'flex';
      dropdownMenu.removeAttribute('inert');
      dropdownMenu.setAttribute('aria-hidden', 'false');
      focusTrap?.activate();
      // Focus first item after a short delay to ensure display is complete
      setTimeout(() => {
        const firstItem = getFocusableElements(dropdownMenu)[0];
        firstItem?.focus();
      }, 10);
    },
    onClose: () => {
      dropdownMenu.style.display = 'none';
      dropdownMenu.setAttribute('inert', '');
      dropdownMenu.setAttribute('aria-hidden', 'true');
      focusTrap?.deactivate();
    }
  });

  // Create focus trap for the dropdown menu
  focusTrap = createFocusTrap(dropdownMenu, {
    returnFocusOnClose: dropdownButton,
    onEscape: () => {
      dropdown.close();
      dropdownButton.focus();
    }
  });

  // Set initial inert state
  dropdownMenu.setAttribute('inert', '');
  dropdownMenu.setAttribute('aria-hidden', 'true');
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupMobileLanguageDropdown);
} else {
  setupMobileLanguageDropdown();
}

// ==================== KEYBOARD NAVIGATION DETECTION ====================

/**
 * Detect keyboard navigation (Tab key) and add class to body
 * This allows us to show focus indicators only when using keyboard
 */
(function() {
  function handleFirstTab(e) {
    if (e.key === 'Tab') {
      document.body.classList.add('user-is-tabbing');
      window.removeEventListener('keydown', handleFirstTab);
      window.addEventListener('mousedown', handleMouseDownOnce);
    }
  }

  function handleMouseDownOnce() {
    document.body.classList.remove('user-is-tabbing');
    window.removeEventListener('mousedown', handleMouseDownOnce);
    window.addEventListener('keydown', handleFirstTab);
  }

  window.addEventListener('keydown', handleFirstTab);
})();